import { c as _c } from "react/compiler-runtime";
import { Flex, VStack } from '@chakra-ui/react';
import { AppLayoutProvider, useParentAppLayout } from '@src/components/sco/AppLayout/AppLayoutContext';
import { AppLayoutPanel, AppLayoutPanelProps } from '@src/components/sco/AppLayout/Panel';
import { FC, ReactNode } from 'react';
export type AppLayoutProps = {
  children?: never;
  colorPalette?: string;
  containerProps?: any;
  footer?: ReactNode;
  header?: ReactNode;
  id?: string;
  panels?: AppLayoutPanelProps[];
  panelProps?: any;
  scrollable?: boolean; // If false, then do not max-height things
};

/** Fill a parent AppLayout panel instead of claiming 100vh. */
export const nestedAppLayoutContainerProps = {
  height: 'full',
  maxHeight: '100%',
  minHeight: 0,
  width: 'full'
};

/** Flush nested panels to the parent (no outer gap/margin). */
export const attachedAppLayoutPanelProps = {
  gap: 0,
  marginX: 0,
  marginBottom: 0,
  paddingX: 0
};

/** Override named -nav/-main/-aside chrome so nested panels sit flush. */
export const attachedPanelContainerProps = {
  bgColor: 'transparent',
  borderRadius: 0,
  border: 'none',
  boxShadow: 'none'
};
const attachedColumnRule = '1px solid {colors.colorPalette.border/40}';

/** Nested left rail — flush, with the vertical rule that used to sit on the Flex split. */
export const attachedNavPanelContainerProps = {
  ...attachedPanelContainerProps,
  borderTop: 'none',
  borderBottom: 'none',
  borderLeft: 'none',
  borderRight: attachedColumnRule
};

/** Nested right aside — flush, with the matching left rule. */
export const attachedAsidePanelContainerProps = {
  ...attachedPanelContainerProps,
  borderTop: 'none',
  borderBottom: 'none',
  borderRight: 'none',
  borderLeft: attachedColumnRule
};

/**
 * AppLayout that fills its parent panel (requests manage, Advisors inner rails).
 * Same API as AppLayout — attaches panels and skips the 100vh shell.
 */
export const NestedAppLayout: FC<AppLayoutProps> = t0 => {
  const $ = _c(14);
  let containerProps;
  let panelProps;
  let rest;
  let t1;
  if ($[0] !== t0) {
    ({
      containerProps,
      panelProps,
      scrollable: t1,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = containerProps;
    $[2] = panelProps;
    $[3] = rest;
    $[4] = t1;
  } else {
    containerProps = $[1];
    panelProps = $[2];
    rest = $[3];
    t1 = $[4];
  }
  const scrollable = t1 === undefined ? false : t1;
  if (rest.children) {
    throw new Error("AppLayout does not support children. Use props instead.");
  }
  let t2;
  if ($[5] !== containerProps) {
    t2 = {
      ...nestedAppLayoutContainerProps,
      ...containerProps
    };
    $[5] = containerProps;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== panelProps) {
    t3 = {
      ...attachedAppLayoutPanelProps,
      ...panelProps
    };
    $[7] = panelProps;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== rest || $[10] !== scrollable || $[11] !== t2 || $[12] !== t3) {
    t4 = <AppLayout scrollable={scrollable} containerProps={t2} panelProps={t3} {...rest} />;
    $[9] = rest;
    $[10] = scrollable;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
};
export const AppLayout: FC<AppLayoutProps> = props => {
  const $ = _c(6);
  if (props.children) {
    throw new Error("AppLayout does not support children. Use props instead.");
  }
  let t0;
  if ($[0] !== props) {
    t0 = <AppLayoutContents {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== props.id || $[3] !== props.panels || $[4] !== t0) {
    t1 = <AppLayoutProvider id={props.id} panels={props.panels}>{t0}</AppLayoutProvider>;
    $[2] = props.id;
    $[3] = props.panels;
    $[4] = t0;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};
export const AppLayoutContents = props => {
  const $ = _c(23);
  const layout = useParentAppLayout();
  const t0 = props.colorPalette || "colorPalette";
  const t1 = props.scrollable === false ? "auto" : "100vh";
  const t2 = props.scrollable === false ? "auto" : "100vh";
  const t3 = props.scrollable === false ? "visible" : "hidden";
  const t4 = props.containerProps;
  const t5 = props.header;
  let t6;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [0, 0, 0.5, 0.5, 1];
    $[0] = t6;
  } else {
    t6 = $[0];
  }
  let t7;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = [0.5, 0.5, 1, 1, 2];
    $[1] = t7;
  } else {
    t7 = $[1];
  }
  let t8;
  let t9;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = [0, 0, 2, 2, 3];
    t9 = {
      "& &::-webkit-scrollbar": {
        width: "16px",
        borderRadius: "8px",
        backgroundColor: "rgba(0, 0, 0, 0.05)"
      },
      "& &::-webkit-scrollbar-thumb": {
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        borderRadius: "8px"
      },
      scrollbarWidth: "thin",
      scrollbarColor: "rgba(85, 84, 115, 1) rgba(0, 0, 0, 0.05)"
    };
    $[2] = t8;
    $[3] = t9;
  } else {
    t8 = $[2];
    t9 = $[3];
  }
  const t10 = props.panelProps;
  let t11;
  if ($[4] !== layout.panels) {
    t11 = layout.panels || [];
    $[4] = layout.panels;
    $[5] = t11;
  } else {
    t11 = $[5];
  }
  let t12;
  if ($[6] !== props.scrollable || $[7] !== t11) {
    let t13;
    if ($[9] !== props.scrollable) {
      t13 = (panel, index) => <AppLayoutPanel key={panel.id || index} {...panel} scrollable={props.scrollable} />;
      $[9] = props.scrollable;
      $[10] = t13;
    } else {
      t13 = $[10];
    }
    t12 = t11.map(t13);
    $[6] = props.scrollable;
    $[7] = t11;
    $[8] = t12;
  } else {
    t12 = $[8];
  }
  let t13;
  if ($[11] !== props.panelProps || $[12] !== t12) {
    t13 = <Flex align="stretch" alignItems="stretch" alignSelf="stretch" direction="row" flex={1} flexGrow={1} gap={t6} justify="stretch" justifyContent="stretch" justifyItems="stretch" justifySelf="stretch" marginX={t7} minHeight={0} overflowX="hidden" overflowY="hidden" paddingBottom={t8} css={t9} {...t10}>{t12}</Flex>;
    $[11] = props.panelProps;
    $[12] = t12;
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  let t14;
  if ($[14] !== props.containerProps || $[15] !== props.footer || $[16] !== props.header || $[17] !== t0 || $[18] !== t1 || $[19] !== t13 || $[20] !== t2 || $[21] !== t3) {
    t14 = <><VStack align="stretch" colorPalette={t0} gap={0} height={t1} id="app-layout" justify="stretch" maxHeight={t2} overflow={t3} width="100%" {...t4}>{t5}{t13}{props.footer}</VStack></>;
    $[14] = props.containerProps;
    $[15] = props.footer;
    $[16] = props.header;
    $[17] = t0;
    $[18] = t1;
    $[19] = t13;
    $[20] = t2;
    $[21] = t3;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  return t14;
};